import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'

import type {
  AllocationProgress,
  AllocationServerError,
  AllocationServerScope,
  VersiServerMessage,
  VersiServerProposal,
  VersiServerStart,
} from '../types/allocationServer'
import {proposalChangesOf, proposalRowsOf} from '../utils/allocationServerAdapter'
import {type AllocationScopeQuery, allocationServerApi as api} from '../utils/allocationServerApi'
import {
  type AllocationRow,
  type ProposalChange,
  isSettingsOnlyProposal,
} from '../utils/teacherAllocationModel'
import {
  type VersiItem,
  clearVersiServerCards,
  readVersiServerCards,
  writeVersiServerCards,
} from '../utils/versiAllocationSession'

type ProposalItem = Extract<VersiItem, {kind: 'proposal'}>
type ClarifyOption = Extract<VersiItem, {kind: 'clarify'}>['options'][number]

type Params = {
  /** The panel's Branch / Level: every message is sent with it (handoff §6.2). */
  scope: AllocationScopeQuery
  /** Grid column labels by key, so proposal cells line up with the grid's subjects. */
  columnNames: ReadonlyMap<string, string>
  /** A clarification option carries a scope: the dropdowns switch to it first (§6.3). */
  onScopeChange: (scope: AllocationScopeQuery) => void
  /**
   * A proposal was applied or undone: progress and the grid changed on the server. `slotKeys`
   * (grid cells, applied only) are the cells it changed.
   */
  onApplied: (overall: AllocationProgress, canContinue: boolean, slotKeys?: string[]) => void
  /** The pending proposal was discarded: the cells it would have changed. */
  onDiscarded?: (slotKeys: string[]) => void
}

let seq = 0
const localId = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${(seq += 1)}`

const proposalStatusOf = (proposal: VersiServerProposal): ProposalItem['status'] => {
  const {status} = proposal
  if (status === 'applied' && proposal.undoneAt) return 'undone'
  return status === 'pending' || status === 'applied' || status === 'discarded'
    ? status
    : 'superseded'
}

const proposalItemOf = (
  proposal: VersiServerProposal,
  columnNames: ReadonlyMap<string, string>
): ProposalItem => ({
  id: `proposal:${proposal.id}`,
  kind: 'proposal',
  message: proposal.text,
  text: proposal.text,
  changes: proposalChangesOf(proposal.changes, columnNames),
  warnings: [],
  status: proposalStatusOf(proposal),
  proposalId: proposal.id,
  projected: {filled: proposal.projected.filled, total: proposal.projected.required},
  saved: {filled: proposal.saved.filled, total: proposal.saved.required},
})

/**
 * One assistant message as conversation items: a clarification with its options, a proposal
 * card (only the pending proposal's content is known after a reload; earlier proposal turns show
 * their text), or a plain reply.
 */
const assistantItemOf = (
  message: VersiServerMessage,
  proposal: VersiServerProposal | null,
  columnNames: ReadonlyMap<string, string>
): VersiItem => {
  const options = message.clarification?.options ?? []
  if (options.length > 0) {
    return {
      id: message.id || localId('c'),
      kind: 'clarify',
      question: message.text,
      options: options.map((option) => ({
        label: option.label,
        employeeId: option.value,
        matchedName: '',
        value: option.value,
        scope: option.scope,
      })),
      answered: false,
    }
  }
  if (message.proposalId && proposal && proposal.id === message.proposalId) {
    return proposalItemOf(proposal, columnNames)
  }
  return {id: message.id || localId('a'), kind: 'assistant', text: message.text}
}

/**
 * The conversation a start (or reset) payload describes, as conversation items. A proposal the
 * server no longer details comes back from its stored card (and result) as last shown.
 */
const itemsOfStart = (
  res: VersiServerStart,
  columnNames: ReadonlyMap<string, string>
): VersiItem[] => {
  const cards = readVersiServerCards()
  const restored = res.messages.flatMap((message): VersiItem[] => {
    if (message.role === 'user') return [{id: message.id, kind: 'user', text: message.text}]
    const item = assistantItemOf(message, res.pendingProposal, columnNames)
    const card = item.kind === 'assistant' && message.proposalId && cards[message.proposalId]
    if (!card) return [item]
    const proposal: VersiItem =
      card.proposal.status === 'pending' ? {...card.proposal, status: 'superseded'} : card.proposal
    return card.result ? [proposal, card.result] : [proposal]
  })
  // Chips only on the latest assistant message.
  const lastIndex = restored.length - 1
  return restored.map((item, idx) =>
    item.kind === 'clarify' && idx !== lastIndex ? {...item, answered: true} : item
  )
}

const closeClarifications = (items: VersiItem[]) =>
  items.map((item) =>
    item.kind === 'clarify' && !item.answered ? {...item, answered: true} : item
  )

const setProposalStatus = (
  items: VersiItem[],
  proposalId: string,
  status: ProposalItem['status']
) =>
  items.map((item) =>
    item.kind === 'proposal' && item.proposalId === proposalId ? {...item, status} : item
  )

type SendBody = {text: string; choice?: string; branchId?: number | null; levelId?: number | null}

/**
 * Versi on the server (handoff §6): the conversation, remembered answers and the pending proposal
 * live on the server, per admin, so reopening the panel resumes it. Versi only proposes; nothing
 * reaches the grid until Apply. Shaped like `useVersiAllocation` so VersiAllocationPage renders
 * either.
 */
export function useVersiServer({
  scope,
  columnNames,
  onScopeChange,
  onApplied,
  onDiscarded,
}: Params) {
  const dispatch = useDispatch()
  const [items, setItems] = useState<VersiItem[]>([])
  const [pendingProposal, setPendingProposal] = useState<VersiServerProposal | null>(null)
  const [start, setStart] = useState<VersiServerStart | null>(null)
  const [examplesCollapsed, setExamplesCollapsed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  /** The saved conversation is being read (panel opened): shown as a skeleton. */
  const [isStarting, setIsStarting] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  /** "New chat" is in flight: the button stays disabled so a second click can't race it. */
  const [isResetting, setIsResetting] = useState(false)
  /** Bumped by each "New chat": the page remounts the thread on it (fresh scroll position). */
  const [chatEpoch, setChatEpoch] = useState(0)
  /** The pending proposal removes subjects: its own approval comes first (§6.6). */
  const [removalApproval, setRemovalApproval] = useState<{count: number} | null>(null)
  /** Batches already undone (or no longer undoable): their Undo is hidden. */
  const [closedUndo, setClosedUndo] = useState<Set<string>>(() => new Set())
  /** Classes every proposal seen so far names, for their section labels. */
  const [proposalRows, setProposalRows] = useState<AllocationRow[]>([])
  /** The scope the server last resolved for the panel (dropdown options). */
  const [serverScope, setServerScope] = useState<AllocationServerScope | null>(null)
  const lastFailedRef = useRef<SendBody | null>(null)
  const scopeRef = useRef(scope)
  scopeRef.current = scope
  const columnNamesRef = useRef(columnNames)
  columnNamesRef.current = columnNames
  const callbacksRef = useRef({onScopeChange, onApplied, onDiscarded})
  callbacksRef.current = {onScopeChange, onApplied, onDiscarded}
  /** A proposal's cells as the grid keys them. */
  const slotsOf = (proposal: VersiServerProposal) =>
    Array.from(
      new Set(proposalChangesOf(proposal.changes, columnNamesRef.current).map((c) => c.slotKey))
    )

  const remember = useCallback((proposal: VersiServerProposal | null | undefined) => {
    if (!proposal) return
    setProposalRows((prev) => {
      const known = new Set(prev.map((row) => row.classKey))
      const fresh = proposalRowsOf(proposal.changes).filter((row) => !known.has(row.classKey))
      return fresh.length > 0 ? [...prev, ...fresh] : prev
    })
  }, [])

  // Proposal cards and their results outlive a reopen (the server only re-sends their text).
  useEffect(() => {
    writeVersiServerCards(items)
  }, [items])

  const push = useCallback((...next: VersiItem[]) => setItems((prev) => [...prev, ...next]), [])
  const notice = useCallback(
    (tone: 'info' | 'warning' | 'error', textKey: string, retry?: boolean) =>
      push({id: localId('n'), kind: 'notice', tone, textKey, ...(retry ? {retry} : {})}),
    [push]
  )

  /** Opens the panel: the saved conversation and pending proposal come back (§6.1). */
  const open = useCallback(() => {
    setIsLoading(true)
    setIsStarting(true)
    api
      .assistantStart(dispatch, scopeRef.current)
      .then((res) => {
        setStart(res)
        setServerScope(res.scope)
        setPendingProposal(res.pendingProposal)
        remember(res.pendingProposal)
        const restored = itemsOfStart(res, columnNamesRef.current)
        setItems(restored)
        if (restored.length > 0) setExamplesCollapsed(true)
      })
      .catch(() => notice('error', 'ERROR'))
      .finally(() => {
        setIsLoading(false)
        setIsStarting(false)
      })
  }, [dispatch, notice, remember])

  const runSend = useCallback(
    (body: SendBody, options?: {retrying?: boolean}) => {
      setExamplesCollapsed(true)
      setItems((prev) => {
        const base = closeClarifications(
          options?.retrying ? prev.filter((item) => !(item.kind === 'notice' && item.retry)) : prev
        )
        return options?.retrying
          ? base
          : [...base, {id: localId('u'), kind: 'user', text: body.text}]
      })
      setIsLoading(true)
      api
        .assistantMessage(dispatch, body)
        .then((turn) => {
          lastFailedRef.current = null
          setServerScope(turn.scope)
          const proposal = turn.proposal
          remember(proposal)
          const next: VersiItem[] = turn.messages
            .filter((message) => message.role === 'assistant')
            .map((message) => assistantItemOf(message, proposal, columnNamesRef.current))
          const pending = proposal && proposal.status === 'pending' ? proposal : null
          if (pending && !next.some((item) => item.kind === 'proposal')) {
            next.push(proposalItemOf(pending, columnNamesRef.current))
          }
          setItems((prev) => {
            // One proposal is pending at a time: a new one supersedes the old.
            const base = pending
              ? prev.map((item) =>
                  item.kind === 'proposal' && item.status === 'pending'
                    ? {...item, status: 'superseded' as const}
                    : item
                )
              : prev
            return [...base, ...next]
          })
          if (pending) setPendingProposal(pending)
        })
        .catch((error: AllocationServerError) => {
          if (error.code === 'ASSISTANT_UNAVAILABLE') {
            lastFailedRef.current = body
            notice('error', 'UNAVAILABLE', true)
            return
          }
          if (error.code === 'EMPTY_MESSAGE') return
          lastFailedRef.current = body
          notice('error', 'ERROR', true)
        })
        .finally(() => setIsLoading(false))
    },
    [dispatch, notice, remember]
  )

  const send = useCallback(
    (text: string, options?: {choice?: string; scope?: AllocationScopeQuery}) => {
      const trimmed = text.trim()
      if (!trimmed || isLoading || isApplying || isResetting) return
      const target = options?.scope ?? scopeRef.current
      runSend({
        text: trimmed,
        ...(options?.choice ? {choice: options.choice} : {}),
        branchId: target.branchId ?? null,
        levelId: target.levelId ?? null,
      })
    },
    [isApplying, isLoading, isResetting, runSend]
  )

  /** Resends the message that failed (`ASSISTANT_UNAVAILABLE`), unchanged. */
  const retry = useCallback(() => {
    const body = lastFailedRef.current
    if (!body || isLoading || isApplying) return
    runSend(body, {retrying: true})
  }, [isApplying, isLoading, runSend])

  /** A clarification chip: switch the scope it names, then send its label with its value. */
  const answerClarification = useCallback(
    (itemId: string, option: ClarifyOption) => {
      if (isLoading || isApplying) return
      let target = scopeRef.current
      if (option.scope) {
        const branchChanged =
          'branchId' in option.scope && option.scope.branchId !== scopeRef.current.branchId
        target = {
          branchId:
            'branchId' in option.scope
              ? (option.scope.branchId ?? null)
              : scopeRef.current.branchId,
          levelId:
            'levelId' in option.scope
              ? (option.scope.levelId ?? null)
              : branchChanged
                ? null
                : scopeRef.current.levelId,
        }
        callbacksRef.current.onScopeChange(target)
      }
      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId && item.kind === 'clarify' ? {...item, answered: true} : item
        )
      )
      send(option.label, {choice: option.value ?? option.employeeId, scope: target})
    },
    [isApplying, isLoading, send]
  )

  const pendingItem = useMemo(() => {
    if (!pendingProposal) return null
    const item = items.find(
      (i) => i.kind === 'proposal' && i.proposalId === pendingProposal.id && i.status === 'pending'
    )
    return item && item.kind === 'proposal' ? item : null
  }, [items, pendingProposal])

  /**
   * Apply (§6.6). A proposal that marks subjects as not taught asks its own approval first
   * (`removalApproval`); `confirmRemoval(true)` sends it. Resolves like useVersiAllocation's.
   */
  const apply = useCallback(
    async (confirm?: unknown): Promise<'applied' | 'stale' | 'error' | 'noop'> => {
      const proposal = pendingProposal
      if (!proposal || !pendingItem || isApplying || isLoading) return 'noop'
      // The page passes the click event: only a literal `true` is an approval.
      const confirmed = confirm === true
      const removals = proposal.changes.filter((change) => change.type === 'not_taught').length
      if (removals > 0 && !confirmed) {
        setRemovalApproval({count: removals})
        return 'noop'
      }
      setRemovalApproval(null)
      setIsApplying(true)
      try {
        const call = () => api.applyProposal(dispatch, proposal.id, confirmed)
        const res = await call().catch((error: AllocationServerError) =>
          error?.code === 'WORKSPACE_BUSY' ? call() : Promise.reject(error)
        )
        if (res.state === 'applied') {
          callbacksRef.current.onApplied(res.overall, res.canContinue, slotsOf(proposal))
          setItems((prev) => [
            ...setProposalStatus(prev, proposal.id, 'applied'),
            {
              id: localId('r'),
              kind: 'result',
              applied: pendingItem.changes.length,
              filled: res.overall.filled,
              total: res.overall.required,
              undoId: res.batchId,
              undone: false,
              settingsOnly: isSettingsOnlyProposal(pendingItem.changes),
            },
          ])
          setPendingProposal(null)
          return 'applied'
        }
        // Cells changed since the proposal: nothing was written; Versi re-planned it.
        const fresh = res.proposal
        remember(fresh)
        setItems((prev) => {
          const next: VersiItem[] = [
            ...setProposalStatus(prev, proposal.id, 'superseded'),
            {id: localId('a'), kind: 'assistant', text: res.message},
          ]
          if (res.state === 'refreshed' && fresh?.status === 'pending') {
            next.push(proposalItemOf(fresh, columnNamesRef.current))
          }
          return next
        })
        setPendingProposal(res.state === 'refreshed' && fresh?.status === 'pending' ? fresh : null)
        return 'stale'
      } catch (raw) {
        const error = raw as AllocationServerError
        if (error?.code === 'REMOVAL_APPROVAL_REQUIRED') {
          setRemovalApproval({count: removals || (error.result?.subjectIds?.length ?? 0)})
          return 'noop'
        }
        if (error?.code === 'PROPOSAL_NOT_PENDING' || error?.code === 'PROPOSAL_NOT_FOUND') {
          // Handled elsewhere (another tab): the panel is opened again from the server.
          open()
          return 'error'
        }
        notice('error', 'APPLY_ERROR')
        return 'error'
      } finally {
        setIsApplying(false)
      }
    },
    [dispatch, isApplying, isLoading, notice, open, pendingItem, pendingProposal, remember]
  )

  const confirmRemoval = useCallback(() => apply(true), [apply])
  const cancelRemoval = useCallback(() => setRemovalApproval(null), [])

  const discard = useCallback(() => {
    const proposal = pendingProposal
    if (!proposal || isApplying) return
    api
      .discardProposal(dispatch, proposal.id)
      .then(() => {
        setItems((prev) => setProposalStatus(prev, proposal.id, 'discarded'))
        setPendingProposal(null)
        notice('info', 'DISCARDED')
        callbacksRef.current.onDiscarded?.(slotsOf(proposal))
      })
      .catch((error: AllocationServerError) => {
        if (error?.code === 'PROPOSAL_NOT_PENDING' || error?.code === 'PROPOSAL_NOT_FOUND') open()
        else notice('error', 'ERROR')
      })
  }, [dispatch, isApplying, notice, open, pendingProposal])

  const undoResult = useCallback(
    (resultId: string) => {
      const item = items.find((i) => i.id === resultId)
      if (!item || item.kind !== 'result' || item.undone) return
      const batchId = item.undoId
      const closeUndo = () => setClosedUndo((prev) => new Set(prev).add(batchId))
      api
        .undo(dispatch, batchId)
        .then((res) => {
          closeUndo()
          // The result card stops claiming its filled count, and the proposal card above it
          // (named by the undo, else the one the card sits under) reads Undone, not Applied.
          setItems((prev) => {
            const at = prev.findIndex((i) => i.id === resultId)
            let proposalId = res.proposalId ?? null
            for (let idx = at - 1; !proposalId && idx >= 0; idx--) {
              const before = prev[idx]
              if (before.kind === 'proposal') proposalId = before.proposalId ?? null
            }
            return prev.map((i) => {
              if (i.id === resultId && i.kind === 'result') return {...i, undone: true}
              if (i.kind === 'proposal' && proposalId && i.proposalId === proposalId) {
                return {...i, status: 'undone' as const}
              }
              return i
            })
          })
          notice('info', 'UNDONE')
          callbacksRef.current.onApplied(res.overall, res.canContinue)
        })
        .catch((error: AllocationServerError) => {
          // Nothing was undone: the result keeps its text, and the button goes.
          closeUndo()
          notice('warning', error?.code === 'CHANGED_SINCE' ? 'UNDO_CHANGED' : 'UNDO_GONE')
        })
    },
    [dispatch, items, notice]
  )

  /**
   * "New chat" (§6.8): the pending proposal is discarded; nothing applied is undone. The reset
   * answers like the start call, so the conversation is replaced from it in one go (never merged
   * into the old list) and the thread remounts at the top.
   */
  const startNewChat = useCallback(() => {
    if (isLoading || isApplying || isResetting) return
    setIsResetting(true)
    api
      .resetAssistant(dispatch, scopeRef.current)
      .then((res) => {
        clearVersiServerCards()
        lastFailedRef.current = null
        const fresh = res?.messages ? itemsOfStart(res, columnNamesRef.current) : []
        if (res?.scope) {
          setStart(res)
          setServerScope(res.scope)
        }
        setPendingProposal(res?.pendingProposal ?? null)
        setRemovalApproval(null)
        setItems(fresh)
        setExamplesCollapsed(fresh.length > 0)
        setChatEpoch((n) => n + 1)
      })
      .catch(() => notice('error', 'ERROR'))
      .finally(() => setIsResetting(false))
  }, [dispatch, isApplying, isLoading, isResetting, notice])

  /** Pending changes by cell, for "Preview in full grid". */
  const previewChanges = useMemo(() => {
    const map = new Map<string, ProposalChange[]>()
    if (!pendingProposal) return map
    proposalChangesOf(pendingProposal.changes, columnNames).forEach((change) => {
      map.set(change.slotKey, [...(map.get(change.slotKey) ?? []), change])
    })
    return map
  }, [columnNames, pendingProposal])

  return {
    items,
    pendingItem,
    pendingProposal,
    previewChanges,
    proposalRows,
    serverScope,
    isLoading,
    isStarting,
    isApplying,
    examplesCollapsed,
    setExamplesCollapsed,
    hasConversation: items.length > 0,
    examples: start
      ? {main: start.examples.map((e) => e.text), more: start.moreExamples.map((e) => e.text)}
      : null,
    open,
    send,
    retry,
    apply,
    confirmRemoval,
    cancelRemoval,
    removalApproval,
    discard,
    undoResult,
    answerClarification,
    canUndo: (undoId: string) => !closedUndo.has(undoId),
    startNewChat,
    canStartNewChat: items.length > 0 && !isLoading && !isApplying && !isResetting,
    chatEpoch,
  }
}

export type VersiServer = ReturnType<typeof useVersiServer>
